import React, { useState } from "react";
import api, { money } from "@/lib/api";
import { GlassCard, PageTitle, LuxuryButton } from "@/components/lux";
import { Search, ChevronDownCircle } from "lucide-react";

function TreeCell({ node, depth, onFocus }) {
  const [open, setOpen] = useState(depth < 2);
  if (!node) return null;
  const hasChildren = node.left || node.right;
  const toneBg = node.status === "active"
    ? "linear-gradient(180deg, #174EA6, #123C8C)"
    : "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))";
  const hasVirtual = (node.left_virtual > 0 || node.right_virtual > 0);
  const kids = open && hasChildren ? (
    <>
      <div className="h-6 w-px" style={{ background: "var(--line)" }} />
      <div className="flex items-start gap-6">
        <div className="flex flex-col items-center">
          <span className="mb-1 text-[10px] font-bold uppercase" style={{ color: "#7fa8ff" }}>Left</span>
          {node.left ? React.createElement(TreeCell, { node: node.left, depth: depth + 1, onFocus }) : <span className="text-xs text-[var(--text-dim)]">Empty</span>}
        </div>
        <div className="flex flex-col items-center">
          <span className="mb-1 text-[10px] font-bold uppercase" style={{ color: "#e08aa0" }}>Right</span>
          {node.right ? React.createElement(TreeCell, { node: node.right, depth: depth + 1, onFocus }) : <span className="text-xs text-[var(--text-dim)]">Empty</span>}
        </div>
      </div>
    </>
  ) : null;
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-44 rounded-xl p-3 text-center card-hover"
        style={{ background: toneBg, border: depth === 0 ? "2px solid #F4C430" : "1px solid var(--line)" }}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold" style={{ color: "#fff" }}>{node.member_id}</p>
          <button onClick={(e) => { e.stopPropagation(); onFocus && onFocus(node.member_id); }} title="Drill in" className="text-[var(--gold)]"><Search size={13} /></button>
        </div>
        <p className="truncate text-xs" style={{ color: "#D7E2F5" }}>{node.name}</p>
        <div className="mt-1.5 flex justify-center gap-2 text-[10px]">
          <span style={{ color: "#7fa8ff" }}>L ${money(node.left_real, 0)}</span>
          <span style={{ color: "#e08aa0" }}>R ${money(node.right_real, 0)}</span>
        </div>
        {node.matched > 0 && <p className="mt-0.5 text-[10px] font-bold gold-text">MATCH ${money(node.matched, 0)}</p>}
        {hasVirtual && <p className="mt-1 rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ background: "rgba(122,24,48,0.5)", color: "#ffd7e0", border: "1px solid rgba(244,196,48,0.4)" }}>VIRTUAL ${money(node.left_virtual + node.right_virtual, 0)}</p>}
        {hasChildren && <button onClick={() => setOpen(!open)} className="mt-1 text-[var(--text-dim)]"><ChevronDownCircle size={16} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} /></button>}
      </div>
      {kids}
    </div>
  );
}

export default function AdminBinary() {
  const [search, setSearch] = useState("");
  const [tree, setTree] = useState(null);
  const load = async (mid) => {
    const q = mid || search;
    try { const r = await api.get(`/user/binary${q ? `?root_member=${q}` : ""}`); setTree(r.data); window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
  };
  return (
    <div className="space-y-6">
      <PageTitle title="Binary Tree" subtitle="Inspect any member's downline — click drill-in to re-root" right={
        <div className="flex gap-2">
          <div className="flex items-center gap-2 rounded-xl border px-3" style={{ borderColor: "var(--line)", background: "rgba(255,255,255,0.05)" }}>
            <Search size={16} className="text-[var(--text-dim)]" />
            <input className="bg-transparent py-2 text-sm text-white outline-none" placeholder="Member ID" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-tree-search" />
          </div>
          <LuxuryButton size="sm" onClick={() => load()} data-testid="admin-tree-load">Load</LuxuryButton>
        </div>
      } />
      <GlassCard className="overflow-x-auto p-8">
        {tree ? <div className="flex min-w-max justify-center">{React.createElement(TreeCell, { node: tree, depth: 0, onFocus: load })}</div> : <p className="text-center text-sm text-[var(--text-dim)]">Enter a Member ID and click Load to view their binary tree.</p>}
      </GlassCard>
    </div>
  );
}
